// client/src/features/salesCoach/components/RepCatalog.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { salesCoachApi } from "../api/salesCoachApi";
import {
  Brain,
  Award,
  Calendar,
  BookOpen,
  ChevronRight,
  FileText,
  CheckCircle,
  ShieldAlert,
  BarChart2,
} from "lucide-react";
import Loader from "@/shared/components/ui/Loaders/Loader";
import SearchBar from "@/shared/components/ui/SearchBar/SearchBar";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";

const RepCatalog = () => {
  const [trainings, setTrainings] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAssessment, setSelectedAssessment] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    loadCatalogData();
  }, []);

  const loadCatalogData = async () => {
    setLoading(true);
    try {
      const allTrainings = await salesCoachApi.listTrainings();
      // Show only published trainings to representative
      setTrainings(allTrainings.filter((t) => t.status === "published"));

      const repHistory = await salesCoachApi.fetchRepHistory();
      setHistory(repHistory);
    } catch (err) {
      console.error("Failed to load catalog data:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredTrainings = trainings.filter(
    (t) =>
      t.product_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.category.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const getScoreColorClass = (score) => {
    if (score >= 85) return "text-green-600 bg-green-50 border-green-200";
    if (score >= 70)
      return "text-secondary bg-secondary-ultra-light border-[#207C9740]";
    if (score >= 50) return "text-amber-600 bg-amber-50 border-amber-200";
    return "text-rose-600 bg-rose-50 border-rose-200";
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader text="Loading training catalog..." />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto bg-transparent h-full flex flex-col min-h-0">
      {/* Header & Search */}
      <div className="flex flex-col md:flex-row md:items-center justify-between pb-6 border-b border-gray-200 dark:border-gray-800 gap-4 shrink-0">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-gray-900 dark:text-gray-100 bg-gradient-to-r from-secondary via-secondary-light to-[#058BB3] bg-clip-text text-transparent">
            Sales Coach Academy
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Practice product training, then roleplay simulations to earn your
            evaluation score.
          </p>
        </div>
        <div className="relative max-w-md w-full">
          <SearchBar
            placeholder="Search trainings or categories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-6 mt-6 pr-1">
        {/* Catalog Grid */}
        <div className="">
          <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center">
            <BookOpen className="w-5 h-5 mr-2 text-secondary" />
            Available Product Trainings
          </h2>
          {filteredTrainings.length === 0 ? (
            <div className="text-center py-12 px-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-sm">
              <Brain className="mx-auto h-12 w-12 text-[#207C9760]" />
              <h3 className="mt-4 text-sm font-bold text-gray-900 dark:text-gray-100">
                No training modules found
              </h3>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Check back later or ask your Org Admin to publish modules.
              </p>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredTrainings.map((t) => (
                <div
                  key={t.id}
                  className="group bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between"
                >
                  <div className="p-6">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-secondary-ultra-light dark:bg-secondary/20 text-secondary dark:text-secondary-light border border-[#207C9730]">
                      {t.category}
                    </span>
                    <h3 className="mt-4 text-xl font-bold text-gray-900 dark:text-gray-100 group-hover:text-secondary dark:group-hover:text-secondary-light transition-colors">
                      {t.product_name}
                    </h3>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 line-clamp-3">
                      {t.description || "No description provided."}
                    </p>
                  </div>
                  <div className="px-6 py-4 bg-gray-50/50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800 flex justify-end">
                    <PrimaryButton
                      className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-white bg-secondary rounded-lg hover:bg-secondary-light transition-all hover:shadow-md hover:shadow-[#207C9720]"
                      onClick={() => {
                        navigate(`/sales-coach/learn/${t.id}`);
                      }}
                      startContent={false}
                      size="md"
                    >
                      Start Training
                      <ChevronRight className="w-4 h-4 ml-1.5" />
                    </PrimaryButton>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* History log */}
        <div className="">
          <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center">
            <Award className="w-5 h-5 mr-2 text-secondary" />
            Your Evaluation History
          </h2>
          {history.length === 0 ? (
            <div className="text-center py-8 px-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-sm text-gray-500 dark:text-gray-400 text-sm font-normal font-sans">
              You haven't completed any evaluated roleplays yet. Master a course
              to build your record!
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800 text-left text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800 text-xs text-gray-400 dark:text-gray-400 uppercase font-semibold">
                    <tr>
                      <th className="px-6 py-3.5">Training Module</th>
                      <th className="px-6 py-3.5">Objection Persona</th>
                      <th className="px-6 py-3.5 text-center">Score</th>
                      <th className="px-6 py-3.5">Assessed Date</th>
                      <th className="px-6 py-3.5 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800 font-medium text-gray-800 dark:text-gray-200">
                    {history.map((r, idx) => (
                      <tr
                        key={idx}
                        className="hover:bg-gray-50/30 dark:hover:bg-gray-800/40 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div>
                            <div className="font-semibold text-gray-900 dark:text-gray-100">
                              {r.product_name}
                            </div>
                            <div className="text-xs uppercase tracking-wide text-secondary dark:text-secondary-light font-bold mt-0.5">
                              {r.mode}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                          {r.persona_name || "General Question Answering"}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span
                            className={`inline-block px-2.5 py-1 rounded-lg text-xs font-bold border ${getScoreColorClass(r.overall_score)}`}
                          >
                            {r.overall_score}%
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-500 dark:text-gray-400 text-xs flex items-center gap-1.5 mt-2">
                          <Calendar className="w-3.5 h-3.5" />
                          {new Date(r.assessed_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={async () => {
                              try {
                                const res = await salesCoachApi.fetchAssessment(
                                  r.id,
                                );
                                setSelectedAssessment({
                                  ...res,
                                  product_name: r.product_name,
                                });
                              } catch (err) {
                                console.error(
                                  "Failed to load assessment details:",
                                  err,
                                );
                              }
                            }}
                            className="px-3 py-1.5 text-xs bg-secondary-ultra-light dark:bg-secondary/20 hover:bg-[#207C9715] text-secondary dark:text-secondary-light rounded-lg transition-colors font-bold flex items-center mx-auto"
                          >
                            <FileText className="w-3.5 h-3.5 mr-1" />
                            View Report
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Drill-down modal */}
        {selectedAssessment &&
          createPortal(
            <div className="fixed inset-0 bg-gray-900/60 dark:bg-black/70 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-3xl max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl relative text-gray-800 dark:text-gray-100 overflow-hidden">
                {/* Fixed Header */}
                <div className="p-6 pb-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                    Evaluation Report
                  </h2>
                  <p className="text-sm text-gray-400 dark:text-gray-400 mt-1">
                    Pitch evaluation for{" "}
                    <strong className="text-gray-800 dark:text-gray-100">{selectedAssessment.product_name}</strong>
                  </p>
                </div>

                {/* Scrollable Body */}
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6 scrollbar-thin scrollbar-thumb-gray-200 dark:scrollbar-thumb-gray-700 scrollbar-track-transparent">
                  {/* Score card grid */}
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="p-4 bg-secondary-ultra-light dark:bg-secondary/20 border border-[#207C9730] dark:border-secondary/40 rounded-2xl col-span-3">
                      <span className="text-xs font-bold text-secondary dark:text-secondary-light uppercase tracking-wider">
                        Overall Score
                      </span>
                      <div className="text-4xl font-extrabold text-secondary dark:text-secondary-light mt-1">
                        {selectedAssessment.overall_score ??
                          selectedAssessment.overallScore}
                        %
                      </div>
                    </div>

                    {[
                      {
                        label: "Product Knowledge",
                        val:
                          selectedAssessment.product_knowledge_score ??
                          selectedAssessment.productKnowledgeScore,
                      },
                      {
                        label: "Objection Handling",
                        val:
                          selectedAssessment.objection_handling_score ??
                          selectedAssessment.objectionHandlingScore,
                      },
                      {
                        label: "Communication",
                        val:
                          selectedAssessment.communication_score ??
                          selectedAssessment.communicationScore,
                      },
                      {
                        label: "Feature Understanding",
                        val:
                          selectedAssessment.feature_understanding_score ??
                          selectedAssessment.featureUnderstandingScore,
                      },
                      {
                        label: "Competitive Positioning",
                        val:
                          selectedAssessment.competitive_understanding_score ??
                          selectedAssessment.competitiveUnderstandingScore,
                      },
                    ].map((item, idx) => (
                      <div
                        key={idx}
                        className="p-3 bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl"
                      >
                        <span className="block text-[10px] text-gray-400 dark:text-gray-400 uppercase tracking-wider font-bold">
                          {item.label}
                        </span>
                        <span className="text-lg font-bold text-gray-700 dark:text-gray-200 mt-1 block">
                          {item.val}%
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Strengths & Weaknesses lists */}
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-sm font-bold text-green-700 dark:text-green-400 flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Key Strengths
                      </h4>
                      <ul className="mt-2 list-disc list-inside text-xs text-gray-650 dark:text-gray-300 space-y-1.5 pl-2 font-medium">
                        {(selectedAssessment.strengths || []).map((s, idx) => (
                          <li key={idx}>{s}</li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="text-sm font-bold text-amber-700 dark:text-amber-400 flex items-center">
                        <ShieldAlert className="w-4 h-4 mr-2" />
                        Areas of Improvement
                      </h4>
                      <ul className="mt-2 list-disc list-inside text-xs text-gray-655 dark:text-gray-300 space-y-1.5 pl-2 font-medium">
                        {(
                          selectedAssessment.improvement_areas ||
                          selectedAssessment.improvementAreas ||
                          []
                        ).map((w, idx) => (
                          <li key={idx}>{w}</li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="text-sm font-bold text-secondary dark:text-secondary-light flex items-center">
                        <BarChart2 className="w-4 h-4 mr-2" />
                        Recommendations
                      </h4>
                      <ul className="mt-2 list-disc list-inside text-xs text-gray-655 dark:text-gray-300 space-y-1.5 pl-2 font-medium">
                        {(selectedAssessment.recommendations || []).map(
                          (r, idx) => (
                            <li key={idx}>{r}</li>
                          ),
                        )}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Fixed Footer */}
                <div className="p-6 pt-4 border-t border-gray-100 dark:border-gray-800 flex justify-end flex-shrink-0">
                  <button
                    onClick={() => setSelectedAssessment(null)}
                    className="px-6 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-800 dark:text-gray-100 text-sm font-semibold rounded-xl transition-all"
                  >
                    Close Report
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
};

export default RepCatalog;
